import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { savePracticeAttempt, startPracticeAttempt } from '../../../api/practiceApi';
import { isStepAnswered } from '../config/workspaceTypes';

export function useWorkspaceState({ item, framework, onAutoSaved }) {
  const [attempt, setAttempt] = useState(null);
  const [responses, setResponses] = useState({});
  const [stepIndex, setStepIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const dirty = useRef(false);
  const saveTimer = useRef(null);
  const onAutoSavedRef = useRef(onAutoSaved);
  useEffect(() => { onAutoSavedRef.current = onAutoSaved; }, [onAutoSaved]);
  const frameworkRef = useRef(framework);
  useEffect(() => { frameworkRef.current = framework; }, [framework]);

  const sourceType = item?.sourceType;
  const sourceKey = item?.sourceKey;

  useEffect(() => {
    if (!sourceType || !sourceKey) return;
    setLoading(true);
    setError('');
    setStepIndex(0);
    startPracticeAttempt({
      source_type: sourceType,
      source_key: sourceKey,
      title: item.title,
      prompt: item.prompt,
      framework_slug: framework,
      context: item.context || {},
    })
      .then((payload) => {
        setAttempt(payload);
        setResponses(payload.responses || {});
        dirty.current = false;
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [framework, sourceType, sourceKey, item?.title, item?.prompt, item?.context]);

  const steps = useMemo(() => attempt?.framework?.steps || [], [attempt]);

  const answeredCount = useMemo(
    () => steps.filter((s) => isStepAnswered(responses[s.key])).length,
    [steps, responses],
  );

  const attemptRef = useRef(null);
  useEffect(() => { attemptRef.current = attempt; }, [attempt]);
  const responsesRef = useRef(responses);
  useEffect(() => { responsesRef.current = responses; }, [responses]);

  const updateStepResponse = useCallback((stepKey, value) => {
    setResponses((prev) => {
      const next = { ...prev, [stepKey]: value };
      responsesRef.current = next;
      return next;
    });
    dirty.current = true;
    setSaveStatus('idle');
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      if (!dirty.current || !attemptRef.current) return;
      setSaveStatus('saving');
      try {
        await savePracticeAttempt(attemptRef.current.attempt_id, responsesRef.current, 'in_progress');
        dirty.current = false;
        setSaveStatus('saved');
        onAutoSavedRef.current?.(responsesRef.current, frameworkRef.current);
      } catch {
        setSaveStatus('error');
      }
    }, 1500);
  }, []);

  const save = useCallback(async (status = 'in_progress') => {
    if (!attemptRef.current) return false;
    clearTimeout(saveTimer.current);
    setSaveStatus('saving');
    try {
      const saved = await savePracticeAttempt(attemptRef.current.attempt_id, responsesRef.current, status);
      setAttempt(saved);
      dirty.current = false;
      setSaveStatus('saved');
      return true;
    } catch (e) {
      setSaveStatus('error');
      return false;
    }
  }, []);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  return {
    attempt,
    responses,
    steps,
    stepIndex,
    setStepIndex,
    loading,
    error,
    saveStatus,
    answeredCount,
    allAnswered: steps.length > 0 && answeredCount === steps.length,
    isCompleted: attempt?.status === 'completed',
    updateStepResponse,
    save,
    isDirty: () => dirty.current,
  };
}
